import { FC, ReactNode, useEffect } from 'react';
import Auth from '@getgo/auth-client';
import { IAuthOptions } from '@getgo/auth-client/lib/auth';
import { ILoginOptions } from '@getgo/auth-client/lib/login';
import { IAuthCodeTokenConfigPkce } from '@getgo/auth-client/lib/pkceToken';
import { captureException } from '@sentry/react';

import TopPageLoader from 'components/top-page-loader';
import config from 'config';

const options: IAuthOptions = {
  client_id: config.CLIENT_ID,
  url: config.AUTH_SERVICE_URL,
  redirect_url: `${window.location.origin}`,
  enableTokenRefresh: true,
};

export const auth = new Auth(options);

const HIJACKING_ERROR = 'hijacking';
const AUTHORIZATION_ERROR = 'failedauthorization';
const INFLIGHT_REQUEST = 'inflight_request';
const tokenName = config.AUTH_TOKEN_STORAGE_KEY;

const cleanupSession = () => {
  sessionStorage.removeItem(tokenName);
  sessionStorage.removeItem(config.STATE);
  sessionStorage.removeItem(config.CODE_VERIFIER);
  sessionStorage.removeItem(INFLIGHT_REQUEST);
};

const populateSession = () => {
  const state = auth.randomString(32);
  const code_verifier = auth.randomString(64);

  sessionStorage.setItem(config.STATE, state);
  sessionStorage.setItem(config.CODE_VERIFIER, code_verifier);
  sessionStorage.setItem(INFLIGHT_REQUEST, window.location.href);

  return { state, code_verifier };
};

export const initiateLoginFlow = () => {
  cleanupSession();
  const { state, code_verifier } = populateSession();

  const config = {
    state,
    code_verifier,
  } as Omit<Partial<ILoginOptions>, 'state'>;

  auth.loginWithPKCE(config);
};

interface GetGoAuthProviderProps {
  children?: ReactNode;
}

const AuthProvider: FC<GetGoAuthProviderProps> = ({ children }) => {
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const stateFromLocation = urlParams.get('state');
    const sessionStoredState = sessionStorage.getItem(config.STATE);
    const token = sessionStorage.getItem(tokenName);
    if (token) {
      return;
    }

    if (stateFromLocation && sessionStoredState) {
      // Throw error if states are different
      if (sessionStoredState !== stateFromLocation) {
        captureException(new Error(HIJACKING_ERROR));
        initiateLoginFlow();
        return;
      }

      // Throw error if auth code is not present
      const authorizationCode = urlParams.get('code');
      if (!authorizationCode) {
        captureException(new Error(AUTHORIZATION_ERROR));
        initiateLoginFlow();
        return;
      }

      const initialCodeVerifier = sessionStorage.getItem(config.CODE_VERIFIER);
      const pkceConfig = {
        state: stateFromLocation,
        code: authorizationCode,
        code_verifier: initialCodeVerifier,
        client_secret: config.CLIENT_SECRET,
      } as Omit<Partial<IAuthCodeTokenConfigPkce>, 'state'>;
      auth.requestPKCEToken(pkceConfig).then((token) => {
        const strToken = JSON.stringify(token);
        sessionStorage.setItem(tokenName, strToken);

        // Redirect to the original page that triggered authentication
        const inflightRequest = sessionStorage.getItem(INFLIGHT_REQUEST);
        window.location.assign(inflightRequest || window.location.origin);
      });
    } else {
      // This condition initiates login if not authenticated
      initiateLoginFlow();
    }
  }, []);

  const token = sessionStorage.getItem(tokenName);

  if (token && token !== 'null') {
    return <>{children}</>;
  }

  return <TopPageLoader />;
};

export default AuthProvider;
